/**
 * ContractorSyncPanel — pulpit synchronizacji KONTRAHENTÓW dla jednej integracji.
 *
 * Układ: statystyki + klucz dopasowania (wspólne dla obu kierunków) na górze, a niżej ZAKŁADKI
 * rozdzielające tematy: Import / Eksport / Automatyzacja. Każda zakładka ma własną akcję i własne
 * wykluczenia (który kontrahent/grupa/kategoria pominąć w TYM trybie).
 *
 * Podpięte do realnej logiki: „Importuj z ERP" (SyncContractorsModal), „Eksportuj do ERP"
 * (ExportContractorsModal, tylko FK) oraz WYKLUCZENIA (zapis do `contractor_exclusions`; przy
 * eksporcie faktycznie filtrują). Automatyzacja + model konfliktów pozostają warstwą UI (czekają
 * na decyzje zespołu / wdrożenie silnika §4a).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Download, Save, Upload } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { MultiSelect } from '@/components/MultiSelect';
import { type RecordCategory } from '@/components/record-categories/RecordCategoryPicker';
import { Select } from '@/components/Select';
import { Tabs, type TabsItem } from '@/components/Tabs';
import { useToast } from '@/contexts/ToastContext';
import { useDictionary } from '@/hooks/useDictionary';
import { api } from '@/lib/api';

import { BidirectionalSyncModal } from './BidirectionalSyncModal';
import { ExportContractorsModal } from './ExportContractorsModal';
import { type ErpIntegrationDto } from './IntegrationsTab';
import { SyncContractorsModal } from './SyncContractorsModal';

const PREFIX = 'tenant.settings.integrations.contractorSync';

type ExclusionOption = { value: string; label: string };

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('pl-PL');
}

export function ContractorSyncPanel({ integration }: { integration: ErpIntegrationDto }) {
  const { t } = useTranslation();

  const isFk = integration.erpSystem === 'symfonia_fk';
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [bidirectionalOpen, setBidirectionalOpen] = useState(false);

  // Kontrolki „do decyzji / bez logiki" — stan lokalny, nic jeszcze nie zapisujemy.
  const [matchKey, setMatchKey] = useState('nip');
  const [autoSync, setAutoSync] = useState(false);
  const [interval, setIntervalValue] = useState('60');
  const [direction, setDirection] = useState('import');

  // Źródła opcji dla wykluczeń — grupy ze słownika, kategorie rekordów, lista kontrahentów.
  const groupsDict = useDictionary('contractor_groups');
  const groupOptions = groupsDict.activeItems.map((item) => ({
    value: item.code,
    label: item.label,
  }));

  const categoriesQ = useQuery({
    queryKey: ['record-categories', 'contractors'],
    queryFn: async () => {
      const res = await api.get<{ categories: RecordCategory[] }>('/record-categories', {
        params: { kartoteka: 'contractors' },
      });
      return res.data.categories;
    },
    staleTime: 60_000,
  });
  const categoryOptions = (categoriesQ.data ?? []).map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const contractorsQ = useQuery({
    queryKey: ['contractor-sync-exclusions', 'contractors', integration.companyId],
    queryFn: async () => {
      const res = await api.get<{ contractors: Array<{ id: string; name: string }> }>(
        '/contractors',
        { params: { companyId: integration.companyId, limit: 200 } },
      );
      return res.data.contractors ?? [];
    },
    staleTime: 60_000,
  });
  const contractorOptions = (contractorsQ.data ?? []).map((c) => ({ value: c.id, label: c.name }));

  const exclusionProps = {
    integrationId: integration.id,
    groupOptions,
    categoryOptions,
    contractorOptions,
    // Źródło opcji jeszcze się wczytuje → pole wyłączone (zamiast mylącego „Brak wyników”).
    loadingOptions: {
      groups: groupsDict.isLoading,
      categories: categoriesQ.isLoading,
      contractors: contractorsQ.isLoading,
    },
  };

  const importTab = (
    <div className="space-y-4">
      <section>
        <p className="mb-2 text-xs text-text-tertiary">{t(`${PREFIX}.importTab.hint`)}</p>
        <Button
          variant="primary"
          onClick={() => setImportOpen(true)}
          data-testid="contractor-sync-import"
        >
          <Download className="h-4 w-4" />
          {t(`${PREFIX}.importOnly`)}
        </Button>
      </section>
      <ExclusionsSection
        {...exclusionProps}
        idPrefix="import"
        scopeLabel={t(`${PREFIX}.tabs.import`)}
      />
    </div>
  );

  const exportTab = (
    <div className="space-y-4">
      <section>
        <p className="mb-2 text-xs text-text-tertiary">{t(`${PREFIX}.exportTab.hint`)}</p>
        <Button
          variant="primary"
          onClick={() => setExportOpen(true)}
          data-testid="contractor-sync-export"
        >
          <Upload className="h-4 w-4" />
          {t(`${PREFIX}.exportOnly`)}
        </Button>
      </section>
      <ExclusionsSection
        {...exclusionProps}
        idPrefix="export"
        scopeLabel={t(`${PREFIX}.tabs.export`)}
      />
    </div>
  );

  const automationTab = (
    <div className="space-y-4">
      {/* Pełna, RĘCZNA synchronizacja dwukierunkowa (silnik konfliktów §4a) — działa dziś, tylko FK. */}
      {isFk && (
        <section className="rounded-lg border border-border-primary bg-bg-secondary p-4">
          <h4 className="text-sm font-semibold text-text-primary">{t(`${PREFIX}.bidir.title`)}</h4>
          <p className="mb-3 mt-1 text-xs text-text-tertiary">{t(`${PREFIX}.bidir.hint`)}</p>
          <Button
            variant="primary"
            onClick={() => setBidirectionalOpen(true)}
            data-testid="contractor-sync-bidirectional"
          >
            <ArrowLeftRight className="h-4 w-4" />
            {t(`${PREFIX}.bidir.button`)}
          </Button>
        </section>
      )}
      <section className="rounded-lg border border-dashed border-border-secondary p-4">
        <div className="mb-1 flex items-center justify-between gap-3">
          <h4 className="text-sm font-semibold text-text-primary">{t(`${PREFIX}.auto.title`)}</h4>
          <span className="rounded-full bg-bg-tertiary px-2 py-0.5 text-[0.68rem] font-medium uppercase tracking-wide text-text-tertiary">
            {t(`${PREFIX}.auto.soonBadge`)}
          </span>
        </div>
        <Checkbox
          label={t(`${PREFIX}.auto.enable`)}
          checked={autoSync}
          onChange={(e) => setAutoSync(e.target.checked)}
        />
        {/* Kierunek automatu: tylko jednokierunkowo (import/eksport) — bez konfliktów.
            „Obie strony" + rozwiązywanie konfliktów wróci ze silnikiem konfliktów (§4a), po decyzji. */}
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Select
            label={t(`${PREFIX}.auto.interval`)}
            value={interval}
            onChange={(e) => setIntervalValue(e.target.value)}
            disabled={!autoSync}
            options={[
              { value: '15', label: t(`${PREFIX}.auto.int15`) },
              { value: '60', label: t(`${PREFIX}.auto.int60`) },
              { value: '360', label: t(`${PREFIX}.auto.int360`) },
              { value: '1440', label: t(`${PREFIX}.auto.int1440`) },
            ]}
          />
          <Select
            label={t(`${PREFIX}.auto.direction`)}
            value={direction}
            onChange={(e) => setDirection(e.target.value)}
            disabled={!autoSync}
            options={[
              { value: 'import', label: t(`${PREFIX}.dir.import`) },
              { value: 'export', label: t(`${PREFIX}.dir.export`) },
              // „Obie strony” — dostępne tylko dla FK (silnik konfliktów §4a); ręczny bieg powyżej.
              ...(isFk ? [{ value: 'both', label: t(`${PREFIX}.dir.both`) }] : []),
            ]}
          />
        </div>
        {/* Automat dotyczy TEJ kartoteki — jesteśmy już w jej zakładce, więc bez wyboru „obszarów". */}
        <p className="mt-3 text-[0.68rem] text-text-tertiary">{t(`${PREFIX}.auto.entitiesHint`)}</p>
      </section>
      <ExclusionsSection
        {...exclusionProps}
        idPrefix="automation"
        scopeLabel={t(`${PREFIX}.tabs.automation`)}
      />
    </div>
  );

  const tabs: TabsItem[] = [
    { value: 'import', label: t(`${PREFIX}.tabs.import`), content: importTab },
    ...(isFk ? [{ value: 'export', label: t(`${PREFIX}.tabs.export`), content: exportTab }] : []),
    { value: 'automation', label: t(`${PREFIX}.tabs.automation`), content: automationTab },
  ];

  return (
    <div className="space-y-6">
      {/* ── Statystyki (wspólne) ───────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label={t(`${PREFIX}.stat.mapped`)} value="—" />
        <Stat label={t(`${PREFIX}.stat.lastSync`)} value={formatDateTime(integration.lastSyncAt)} />
        <Stat
          label={t(`${PREFIX}.stat.status`)}
          badge={integration.isActive ? 'active' : 'inactive'}
          badgeText={
            integration.isActive
              ? t('tenant.settings.integrations.list.statusActive')
              : t('tenant.settings.integrations.list.statusInactive')
          }
        />
      </div>

      {/* ── Klucz dopasowania (wspólny dla importu i eksportu) ──── */}
      <section className="rounded-lg border border-border-primary bg-bg-secondary p-4">
        <Select
          label={t(`${PREFIX}.matchKey.label`)}
          value={matchKey}
          onChange={(e) => setMatchKey(e.target.value)}
          // Klucz dopasowania: TYLKO NIP + Kod (decyzja Bartosza 2026-09-01). ID/Pozycja świadomie
          // usunięte — kontrahent maczowany po NIPie, a bez NIP-u po kodzie (unikalny skrót).
          options={[
            { value: 'nip', label: t(`${PREFIX}.matchKey.nip`) },
            { value: 'code', label: t(`${PREFIX}.matchKey.code`) },
          ]}
          className="max-w-xs"
        />
        <p className="mt-2 text-xs text-text-tertiary">{t(`${PREFIX}.matchKey.hint`)}</p>
      </section>

      {/* ── Zakładki: Import / Eksport / Automatyzacja ─────────── */}
      <Tabs idPrefix="contractor-sync" items={tabs} />

      <SyncContractorsModal
        open={importOpen}
        integrationId={integration.id}
        integrationName={integration.name}
        onClose={() => setImportOpen(false)}
      />
      <ExportContractorsModal
        open={exportOpen}
        integrationId={integration.id}
        integrationName={integration.name}
        onClose={() => setExportOpen(false)}
      />
      <BidirectionalSyncModal
        open={bidirectionalOpen}
        integrationId={integration.id}
        integrationName={integration.name}
        onClose={() => setBidirectionalOpen(false)}
      />
    </div>
  );
}

interface ExclusionRuleSet {
  groups: string[];
  categories: string[];
  contractors: string[];
}
type DirectionExclusions = Partial<Record<'import' | 'export' | 'automation', ExclusionRuleSet>>;

/**
 * Wykluczenia z synchronizacji dla jednego kierunku (import / eksport / automat). Ładuje i zapisuje
 * reguły do `erp_integrations.contractor_exclusions` (per kierunek). Filtrowanie działa dziś przy
 * EKSPORCIE (my→FK); dla importu/automatu reguły są zapisywane, ale jeszcze nie stosowane.
 */
function ExclusionsSection({
  integrationId,
  groupOptions,
  categoryOptions,
  contractorOptions,
  loadingOptions,
  idPrefix,
  scopeLabel,
}: {
  integrationId: string;
  groupOptions: ExclusionOption[];
  categoryOptions: ExclusionOption[];
  contractorOptions: ExclusionOption[];
  loadingOptions: { groups: boolean; categories: boolean; contractors: boolean };
  idPrefix: string;
  scopeLabel: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [skipGroups, setSkipGroups] = useState<string[]>([]);
  const [skipCategories, setSkipCategories] = useState<string[]>([]);
  const [skipContractors, setSkipContractors] = useState<string[]>([]);

  const dataQ = useQuery({
    queryKey: ['erp-contractor-exclusions', integrationId],
    queryFn: async () => {
      const res = await api.get<{ exclusions: DirectionExclusions }>(
        `/erp-integrations/${integrationId}/contractor-exclusions`,
      );
      return res.data.exclusions ?? {};
    },
  });

  useEffect(() => {
    const rules = dataQ.data?.[idPrefix as 'import' | 'export' | 'automation'];
    setSkipGroups(rules?.groups ?? []);
    setSkipCategories(rules?.categories ?? []);
    setSkipContractors(rules?.contractors ?? []);
  }, [dataQ.data, idPrefix]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const all: DirectionExclusions = {
        ...(dataQ.data ?? {}),
        [idPrefix]: {
          groups: skipGroups,
          categories: skipCategories,
          contractors: skipContractors,
        },
      };
      await api.put(`/erp-integrations/${integrationId}/contractor-exclusions`, {
        exclusions: all,
      });
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.settings.integrations.contractorSync.exclusions.saved'),
      });
      queryClient.invalidateQueries({ queryKey: ['erp-contractor-exclusions', integrationId] });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.settings.integrations.contractorSync.exclusions.saveError'),
      });
    },
  });

  return (
    <section className="rounded-lg border border-dashed border-border-secondary p-4">
      <div className="mb-1 flex items-center justify-between gap-3">
        <h4 className="text-sm font-semibold text-text-primary">
          {t(`${PREFIX}.exclusions.title`)}
          <span className="ml-1.5 font-normal text-text-tertiary">· {scopeLabel}</span>
        </h4>
      </div>
      <p className="mb-3 text-xs text-text-tertiary">
        {idPrefix === 'automation'
          ? t(`${PREFIX}.exclusions.automationHint`)
          : t(`${PREFIX}.exclusions.hint`)}
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MultiSelect
          label={t(`${PREFIX}.exclusions.groups`)}
          value={skipGroups}
          onChange={setSkipGroups}
          options={groupOptions}
          disabled={dataQ.isLoading || loadingOptions.groups}
          placeholder={t(`${PREFIX}.exclusions.groupsPlaceholder`)}
          data-testid={`contractor-sync-skip-groups-${idPrefix}`}
        />
        <MultiSelect
          label={t(`${PREFIX}.exclusions.categories`)}
          value={skipCategories}
          onChange={setSkipCategories}
          options={categoryOptions}
          disabled={dataQ.isLoading || loadingOptions.categories}
          placeholder={t(`${PREFIX}.exclusions.categoriesPlaceholder`)}
          data-testid={`contractor-sync-skip-categories-${idPrefix}`}
        />
        <MultiSelect
          label={t(`${PREFIX}.exclusions.contractors`)}
          value={skipContractors}
          onChange={setSkipContractors}
          options={contractorOptions}
          disabled={dataQ.isLoading || loadingOptions.contractors}
          placeholder={t(`${PREFIX}.exclusions.contractorsPlaceholder`)}
          data-testid={`contractor-sync-skip-contractors-${idPrefix}`}
        />
      </div>
      <div className="mt-3 flex justify-end">
        <Button
          size="sm"
          leftIcon={<Save className="h-4 w-4" />}
          loading={saveMutation.isPending}
          // Bez wczytanych reguł zapis nadpisałby pozostałe zakładki pustymi listami.
          disabled={dataQ.data === undefined}
          onClick={() => saveMutation.mutate()}
          data-testid={`contractor-sync-save-exclusions-${idPrefix}`}
        >
          {t(`${PREFIX}.exclusions.save`)}
        </Button>
      </div>
    </section>
  );
}

function Stat({
  label,
  value,
  badge,
  badgeText,
}: {
  label: string;
  value?: string;
  badge?: 'active' | 'inactive';
  badgeText?: string;
}) {
  return (
    <div className="rounded-lg border border-border-primary bg-bg-secondary p-4">
      <div className="text-[0.68rem] font-medium uppercase tracking-wide text-text-tertiary">
        {label}
      </div>
      {badge ? (
        <span
          className={`mt-2 inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
            badge === 'active'
              ? 'bg-green-500/10 text-green-700 dark:text-green-400'
              : 'bg-bg-tertiary text-text-tertiary'
          }`}
        >
          {badgeText}
        </span>
      ) : (
        <div className="mt-1 text-lg font-semibold text-text-primary">{value}</div>
      )}
    </div>
  );
}
